import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { useTheme } from '../contexts/ThemeContext';

const Portfolio = () => {
  const sectionRef = useRef(null);
  const projectsRef = useRef(null);
  const { isDark } = useTheme();
  const [activeFilter, setActiveFilter] = useState('All');

  const projects = [
    {
      title: "E-Commerce Platform",
      category: "Web Development",
      description: "A modern e-commerce platform with advanced features including real-time inventory, payment processing, and analytics dashboard.",
      image: "https://img.freepik.com/free-photo/close-up-man-shopping-with-laptop_23-2149241375.jpg?t=st=1761301428~exp=1761305028~hmac=063dcdb19156d591e3bb6c4d9a196a7fd29b2528fa2c3f7d86d4caca6c34d3f0&w=1480",
      technologies: ["React", "Node.js", "MongoDB", "Stripe"],
      link: "#"
    },
    {
      title: "Mobile Banking App",
      category: "Mobile Development",
      description: "Secure mobile banking application with biometric authentication, real-time transactions, and financial insights.",
      image: "https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      technologies: ["React Native", "Firebase", "Node.js", "AWS"],
      link: "#"
    },
    {
      title: "Healthcare Management System",
      category: "Web Development",
      description: "Comprehensive healthcare management system for patient records, appointment scheduling, and telemedicine features.",
      image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      technologies: ["Vue.js", "Python", "PostgreSQL", "Docker"],
      link: "#"
    },
    {
      title: "AI-Powered Analytics Dashboard",
      category: "Data Analytics",
      description: "Intelligent analytics dashboard with machine learning capabilities for business intelligence.",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      technologies: ["Python", "TensorFlow", "React", "D3.js"],
      link: "#"
    },
    {
      title: "Real Estate Platform",
      category: "Web Development",
      description: "Full-featured real estate platform with property listings, virtual tours, and integrated CRM system.",
      image: "https://img.freepik.com/free-photo/real-estate-accommodation-property-investment-graphic-word_53876-138587.jpg?t=st=1761301528~exp=1761305128~hmac=1912b73cbee18f1baa8b484079521d1ed49f039ba57d88bcdfce02412f037588&w=1480",
      technologies: ["Next.js", "Prisma", "PostgreSQL", "Cloudinary"],
      link: "#"
    },
    {
      title: "IoT Monitoring System",
      category: "IoT Solutions",
      description: "Real-time IoT monitoring system for industrial equipment with predictive maintenance.",
      image: "https://images.unsplash.com/photo-1573164713988-8665fc963095?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      technologies: ["React", "Node.js", "MQTT", "InfluxDB"],
      link: "#"
    }
  ];

  // Filter projects based on active filter
  const filteredProjects = activeFilter === 'All' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  useEffect(() => {
    const projectsContainer = projectsRef.current;
    if (!projectsContainer) return;

    const projects = Array.from(projectsContainer.children);
    if (projects.length === 0) return;

    // Stagger animation for project cards
    gsap.fromTo(projects,
      { y: 80, opacity: 0, scale: 0.9 },
      {
        y: 0,
        opacity: 1,
        scale: 1,
        duration: 0.8,
        stagger: 0.15,
        ease: "power3.out",
        scrollTrigger: {
          trigger: projectsContainer,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse"
        }
      }
    );

    // Hover animations for project cards
    projects.forEach(project => {
      const overlay = project.querySelector('.project-overlay');
      const content = project.querySelector('.project-content');
      
      const hoverTl = gsap.timeline({ paused: true });
      
      hoverTl
        .to(project, {
          y: -10,
          scale: 1.02,
          duration: 0.3,
          ease: "power2.out"
        })
        .to(overlay, {
          opacity: 1,
          duration: 0.3
        }, 0)
        .to(content, {
          y: 0,
          opacity: 1,
          duration: 0.3
        }, 0.1);

      project.addEventListener('mouseenter', () => hoverTl.play());
      project.addEventListener('mouseleave', () => hoverTl.reverse());
    });

  }, []);

  // Re-run animations when filtered projects change
  useEffect(() => {
    const projectsContainer = projectsRef.current;
    if (!projectsContainer) return;

    const projects = Array.from(projectsContainer.children);
    if (projects.length === 0) return;

    // Re-apply stagger animation for new filtered projects
    gsap.fromTo(projects,
      { y: 80, opacity: 0, scale: 0.9 },
      {
        y: 0,
        opacity: 1,
        scale: 1,
        duration: 0.8,
        stagger: 0.15,
        ease: "power3.out"
      }
    );

    // Re-apply hover animations for new filtered projects
    projects.forEach(project => {
      const overlay = project.querySelector('.project-overlay');
      const content = project.querySelector('.project-content');
      
      const hoverTl = gsap.timeline({ paused: true });
      
      hoverTl
        .to(project, {
          y: -10,
          scale: 1.02,
          duration: 0.3,
          ease: "power2.out"
        })
        .to(overlay, {
          opacity: 1,
          duration: 0.3
        }, 0)
        .to(content, {
          y: 0,
          opacity: 1,
          duration: 0.3
        }, 0.1);

      project.addEventListener('mouseenter', () => hoverTl.play());
      project.addEventListener('mouseleave', () => hoverTl.reverse());
    });

  }, [filteredProjects]);

  return (
    <section id="portfolio" ref={sectionRef} className="pb-14 pt-8 bg-blue-50 dark:bg-gray-900 relative">
      {/* Section Separator */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-300 dark:via-primary-600 to-transparent"></div>
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-primary-800 dark:text-primary-100 mb-4">
            Our <span className="text-accent-600 dark:text-accent-400">Portfolio</span>
          </h2>
          <p className="text-lg text-primary-600 dark:text-primary-300 max-w-3xl mx-auto">
            Explore our recent projects and see how we've helped businesses transform their digital presence 
            with innovative solutions and cutting-edge technology.
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {['All', 'Web Development', 'Mobile Development', 'Data Analytics', 'IoT Solutions'].map((filter, index) => (
            <button
              key={index}
              onClick={() => setActiveFilter(filter)}
              className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                activeFilter === filter
                  ? 'bg-accent-600 text-white shadow-lg' 
                  : 'bg-white dark:bg-gray-800 text-primary-600 dark:text-primary-300 hover:bg-accent-50 dark:hover:bg-accent-900/20 hover:text-accent-600 dark:hover:text-accent-400 border border-primary-200 dark:border-gray-700'
              }`}
            >
              {filter}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div ref={projectsRef} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project, index) => (
            <div
              key={index}
              className="bg-gray-200 dark:bg-gray-800 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group cursor-pointer"
            >
              {/* Project Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="project-image w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                
                {/* Overlay */}
                <div className="project-overlay absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 transition-opacity duration-300">
                  <div className="project-content absolute bottom-0 left-0 right-0 p-6 transform translate-y-4 opacity-0">
                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.technologies.map((tech, techIndex) => (
                        <span
                          key={techIndex}
                          className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                    <button className="w-full bg-white text-gray-900 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-300">
                      View Project
                    </button>
                  </div>
                </div>
              </div>

              {/* Project Info */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-primary-600 dark:text-accent-400 bg-primary-50 dark:bg-accent-900/20 px-3 py-1 rounded-full">
                    {project.category}
                  </span>
                </div>
                
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                  {project.title}
                </h3>
                
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                  {project.description}
                </p>
              </div>
            </div>
          ))}
        </div>

       
      </div>
      {/* Section Separator */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-300 dark:via-primary-600 to-transparent"></div>
    </section>
  );
};

export default Portfolio;
